import type { PrivateKey as IPrivateKey } from 'starkbank-ecdsa';
import { createResetSessionAuthHeaders } from './reset-session';
import { BackendValidationError, type BackendErrorResponse } from './user-invite';

export interface Phone {
  id: string;
  status: 'created' | 'pending' | 'active' | 'canceled' | 'failed' | 'expired';
  owner_type: string;
  owner_id: string;
  code: string;
  number: string;
  expiration: string;
  resends: number;
  created: string;
}

export interface PhoneListResponse {
  phones: Phone[];
  cursor?: string;
  message?: string;
}

export interface PhoneSingleResponse {
  phone: Phone;
  message?: string;
}

async function handlePhoneErrors(response: Response): Promise<never> {
  let errorData: BackendErrorResponse;
  try {
    errorData = await response.json();

    if (errorData.errors && Array.isArray(errorData.errors) && errorData.errors.length > 0) {
      const errorMessages = errorData.errors.map(e => e.message).filter(Boolean);
      const combinedMessage = errorMessages.length > 0
        ? errorMessages.join('; ')
        : 'Validation error';

      throw new BackendValidationError(
        combinedMessage,
        errorData.errors[0]?.code,
        errorData.errors[0]?.parameter,
        errorData.errors,
      );
    }

    if (errorData.code && errorData.message) {
      throw new BackendValidationError(errorData.message, errorData.code, errorData.parameter);
    }

    throw new BackendValidationError(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
  } catch (e) {
    if (e instanceof BackendValidationError) {
      throw e;
    }
    throw new BackendValidationError(`HTTP ${response.status}: ${response.statusText}`);
  }
}

/**
 * Create a Phone for the current session user. Dispatches an SMS token.
 * Idempotent on (owner, code, number) — if a pending Phone already exists for
 * the same number, the backend fires a resend on it instead of creating a new
 * resource.
 */
export async function createPhone(
  sessionId: string,
  sessionPrivateKey: IPrivateKey,
  code: string,
  number: string,
): Promise<PhoneListResponse> {
  const body = { phones: [{ code, number }] };
  const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey, body);

  const response = await fetch('/api/phone', {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await handlePhoneErrors(response);
  }
  return (await response.json()) as PhoneListResponse;
}

/**
 * Submit the SMS token. On success the Phone transitions to `active`.
 */
export async function submitPhoneToken(
  phoneId: string,
  sessionId: string,
  sessionPrivateKey: IPrivateKey,
  token: string,
): Promise<PhoneSingleResponse> {
  const body = { token };
  const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey, body);

  const response = await fetch(`/api/phone/${phoneId}/token`, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    await handlePhoneErrors(response);
  }
  return (await response.json()) as PhoneSingleResponse;
}

/**
 * List the current session user's Phone resources. Optionally filter by status.
 */
export async function listPhones(
  sessionId: string,
  sessionPrivateKey: IPrivateKey,
  status?: Phone['status'],
): Promise<PhoneListResponse> {
  const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey);
  const query = status ? `?status=${encodeURIComponent(status)}` : '';
  const response = await fetch(`/api/phone${query}`, { method: 'GET', headers });

  if (!response.ok) {
    await handlePhoneErrors(response);
  }
  return (await response.json()) as PhoneListResponse;
}

/**
 * Resend the SMS token. Throttled to 1/min and capped at 3 total resends
 * per Phone — the backend returns an error otherwise.
 */
export async function resendPhoneToken(
  phoneId: string,
  sessionId: string,
  sessionPrivateKey: IPrivateKey,
): Promise<PhoneSingleResponse> {
  const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey);

  const response = await fetch(`/api/phone/${phoneId}/resend`, {
    method: 'POST',
    headers,
  });

  if (!response.ok) {
    await handlePhoneErrors(response);
  }
  return (await response.json()) as PhoneSingleResponse;
}
